import React, { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import { useTranslation } from 'react-i18next';
import { toIntlDateTimeFormat } from '@zedoc/date';
import Button from '../Button';
import Select from '../Select';
import {
  AppointmentRecord,
  MedicationRecord,
  ProcedureRecord,
} from '../EpisodeOfCareRecord';

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

// Format date as YYYY-MM-DD in local timezone (avoids UTC conversion issues)
const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isSameDay = (a, b) => a.toDateString() === b.toDateString();

const generateDays = (language, startDate, daysToShow) =>
  Array.from({ length: daysToShow }, (_, i) => {
    const date = addDays(startDate, i);
    return {
      date,
      label: toIntlDateTimeFormat(language, date, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
      shortLabel: toIntlDateTimeFormat(language, date, {
        weekday: 'short',
        day: 'numeric',
      }),
      isToday: isSameDay(date, new Date()),
    };
  });

const CalendarTimeView = ({
  events,
  isLoading,
  jumpToDate,
  onDateRangeChange,
  className,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const timeSlots = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => {
        const date = new Date();
        date.setHours(i, 0, 0, 0); // 00:00 to 23:00
        return {
          hour: i,
          label: toIntlDateTimeFormat(language, date, {
            hour: 'numeric',
            minute: '2-digit',
          }),
        };
      }),
    [language],
  );

  const [startDate, setStartDate] = useState(startOfDay(new Date()));
  const [daysToShow, setDaysToShow] = useState(5);
  const nowRef = useRef(null);

  const days = useMemo(
    () => generateDays(language, startDate, daysToShow),
    [language, startDate, daysToShow],
  );

  // Notify parent when date range changes
  useEffect(() => {
    if (onDateRangeChange) {
      const endDate = addDays(startDate, daysToShow - 1);
      onDateRangeChange({
        startDate: formatDateLocal(startDate),
        endDate: formatDateLocal(endDate),
      });
    }
  }, [startDate, daysToShow, onDateRangeChange]);

  const goToPreviousDays = () => setStartDate(addDays(startDate, -daysToShow));
  const goToNextDays = () => setStartDate(addDays(startDate, daysToShow));
  const goToToday = () => setStartDate(startOfDay(new Date()));

  useEffect(() => {
    if (jumpToDate) {
      setStartDate(startOfDay(jumpToDate));
      setDaysToShow(1);
    }
  }, [jumpToDate]);

  // Scroll to current time on mount so it appears in the center of the view
  useEffect(() => {
    if (nowRef.current) {
      nowRef.current.scrollIntoView({ block: 'center' });
    }
  }, []);

  return (
    <div
      className={`flex flex-col bg-surface border border-divider rounded-md ${className}`}
    >
      <div className="p-4 border-b border-divider">
        <div className="flex items-center justify-between">
          <div className="cluster-2 items-center">
            <div className="cluster-2 items-center">
              <Button
                data-testid="button-go-to-previous-day-range"
                type="tertiary"
                onClick={goToPreviousDays}
                icon={<ChevronLeftIcon />}
              />
              <span className="font-semibold">
                {toIntlDateTimeFormat(language, days[0].date, {
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                -{' '}
                {toIntlDateTimeFormat(language, days[days.length - 1].date, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
              <Button
                data-testid="button-go-to-next-day-range"
                type="tertiary"
                onClick={goToNextDays}
                icon={<ChevronRightIcon />}
              />
            </div>
          </div>

          <div className="cluster-4 items-center">
            <Select
              data-testid="select-day-range"
              options={[
                { label: t('daysWithCount_one', { count: 1 }), value: 1 },
                { label: t('daysWithCount', { count: 3 }), value: 3 },
                { label: t('daysWithCount', { count: 5 }), value: 5 },
                { label: t('daysWithCount', { count: 7 }), value: 7 },
              ]}
              value={daysToShow}
              onChange={(val) => setDaysToShow(Number(val))}
            />
            <div className="h-4 w-px bg-divider" />
            <Button
              data-testid="button-go-to-today"
              type="ghost"
              size="medium"
              onClick={goToToday}
            >
              {t('today')}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-y-auto min-h-0 items-start">
        <div className="w-20 flex-shrink-0 border-r border-divider">
          <div className="h-12 border-b border-divider flex" />
          {timeSlots.map((slot) => (
            <div
              key={slot.hour}
              className="h-24 border-b border-divider flex items-start justify-center pt-2 text-xs font-medium last:border-none"
            >
              {slot.label}
            </div>
          ))}
        </div>

        <div className="flex-1 relative">
          <div className="flex h-12 border-b border-divider sticky top-0 bg-surface z-30">
            {days.map((day, index) => (
              <div
                key={index}
                className="flex-1 flex items-center justify-center border-r border-divider last:border-none"
                style={day.isToday ? { backgroundColor: '#3b82f608' } : {}}
              >
                <div className="text-center">
                  <div
                    className={`text-xs font-medium ${
                      day.isToday ? 'text-primary' : ''
                    }`}
                  >
                    {day.shortLabel}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="relative">
            {timeSlots.map((slot) => (
              <div
                key={slot.hour}
                className="h-24 flex border-b border-gray-100 dark:border-gray-600"
              >
                {days.map((day, i) => (
                  <div
                    key={i}
                    className="flex-1 border-r border-gray-100 dark:border-gray-600 last:border-none"
                    style={day.isToday ? { backgroundColor: '#3b82f608' } : {}}
                  />
                ))}
              </div>
            ))}

            {days.some((day) => day.isToday) && (
              <div
                ref={nowRef}
                className="absolute left-0 right-0 border-t-2 border-primary z-20"
                style={{
                  top: `${
                    new Date().getHours() * 96 +
                    (new Date().getMinutes() / 60) * 96
                  }px`,
                }}
              >
                <div className="text-xs bg-primary text-on-primary px-2 py-0.5 rounded-md ml-4 -mt-3">
                  {t('now')}
                </div>
              </div>
            )}

            {!isLoading &&
              events.map((event) => {
                const date = event.start;
                const duration = (event.end - event.start) / 60000;
                const hourIndex = timeSlots.findIndex(
                  (slot) => slot.hour === date.getHours(),
                );
                const dayIndex = days.findIndex((d) => isSameDay(d.date, date));
                if (dayIndex === -1 || hourIndex === -1) return null;

                const hourHeight = 96;
                const top =
                  hourIndex * hourHeight +
                  (date.getMinutes() / 60) * hourHeight;
                const height = Math.max(40, (duration / 60) * hourHeight);
                const width = `${100 / days.length - 2}%`;
                const left = `${(dayIndex / days.length) * 100 + 1}%`;

                if (event.domain === 'procedure') {
                  return (
                    <ProcedureRecord
                      procedure={event}
                      showTime={height > 60}
                      showClinician={height > 80}
                      showLocation={height > 100}
                      style={{
                        position: 'absolute',
                        top: `${top + 4}px`,
                        height: `${height - 8}px`,
                        width,
                        left,
                      }}
                    />
                  );
                }

                if (event.domain === 'drug') {
                  return (
                    <MedicationRecord
                      medication={event}
                      showTime={height > 60}
                      showClinician={height > 80}
                      showLocation={height > 100}
                      style={{
                        position: 'absolute',
                        top: `${top + 4}px`,
                        height: `${height - 8}px`,
                        width,
                        left,
                      }}
                    />
                  );
                }

                return (
                  <AppointmentRecord
                    appointment={event}
                    showTime={height > 60}
                    showClinician={height > 80}
                    showLocation={height > 100}
                    style={{
                      position: 'absolute',
                      top: `${top + 4}px`,
                      height: `${height - 8}px`,
                      width,
                      left,
                    }}
                  />
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

CalendarTimeView.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      patientName: PropTypes.string,
      patientId: PropTypes.string,
      clinician: PropTypes.string,
      start: PropTypes.instanceOf(Date).isRequired,
      location: PropTypes.string,
      status: PropTypes.string,
      appointmentType: PropTypes.string,
      appointmentDescription: PropTypes.string,
      projectId: PropTypes.string,
      milestoneId: PropTypes.string,
      internalStatus: PropTypes.oneOf(['Active', 'Archived', 'Pending']),
      processed: PropTypes.bool,
      notes: PropTypes.string,
    }),
  ).isRequired,
  isLoading: PropTypes.bool,
  jumpToDate: PropTypes.instanceOf(Date),
  onDateRangeChange: PropTypes.func,
  className: PropTypes.string,
};

CalendarTimeView.defaultProps = {
  isLoading: false,
  jumpToDate: null,
  onDateRangeChange: null,
  className: '',
};

export default CalendarTimeView;
